import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { repAPI } from '../../services/api';

const MyClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      // Get user from sessionStorage
      const userStr = sessionStorage.getItem('barebiologics_currentUser');
      if (!userStr) {
        setError('User not found. Please log in again.');
        setLoading(false);
        return;
      }
      const user = JSON.parse(userStr);
      const response = await repAPI.getRepClients(user.id);
      if (response.success) {
        setClients(response.data);
      }
    } catch (err) {
      setError('Failed to load clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {clients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontFamily: 'Poppins, sans-serif' }}>
            No clients assigned yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Clients will appear here once they are assigned to you
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              {clients.length} {clients.length === 1 ? 'Client' : 'Clients'}
            </Typography>
            <Chip 
              label={`${clients.length} Total`} 
              color="primary" 
              sx={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Clinic Name
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Contact Name
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Phone
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, py: 2 }}>
                    Assigned Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((rel) => (
                  <TableRow key={rel.id} hover>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                        {rel.client.profile?.clinicName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Poppins, sans-serif', py: 2 }}>
                      {rel.client.profile?.firstName} {rel.client.profile?.lastName}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                          {rel.client.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Poppins, sans-serif', py: 2 }}>
                      {rel.client.profile?.phone || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Poppins, sans-serif', py: 2 }}>
                      {rel.client.profile?.city && rel.client.profile?.state
                        ? `${rel.client.profile.city}, ${rel.client.profile.state}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Poppins, sans-serif', py: 2 }}>
                      {new Date(rel.assignedDate).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default MyClients;
